import Axios, { CancelTokenSource } from 'axios';
import EnvironmentService from '@/env/EnvironmentService';
import SimulationApi from '@/modules/calculator-v25/services/shared/SimulationApi';
import type {
  MortgageAddOffersToDealRequest,
  MortgageCreateSimulationRequest,
  MortgageCreateSimulationResponse,
  MortgageDuplicateLoanRequest,
  MortgageDuplicateLoanResponse,
  MortgageGetHomeStartAssumptionsRequest,
  MortgageGetHomeStartAssumptionsResponse,
  MortgageGetOffersByIdsRequest,
  MortgageGetOffersByIdsResponse,
  MortgageGetScheduleRequest,
  MortgageGetScheduleResponse,
  MortgageGetSimulationByIdResponse,
  MortgageIsLoanAvailableRequest,
  MortgageIsLoanAvailableResponse,
  MortgagePinOfferResponse,
  MortgageRemoveDuplicateRequest,
  MortgageRemoveDuplicateResponse,
  MortgageSelectOfferResponse,
  MortgageUnpinOfferResponse,
  MortgageUnselectOfferResponse,
  MortgageUpdatePersistentSimulationByIdRequest,
  MortgageUpdatePersistentSimulationByIdResponse,
  MortgageUpdateSimulationByIdRequest,
  MortgageUpdateSimulationByIdResponse,
} from '@/modules/calculator-v25/types/mortgage/dto/endpoints';

class MortgageSimulationApi {
  private client = SimulationApi.client;

  private persistentSimulationRequest: Nullable<CancelTokenSource> = null;

  private get apiUrl(): string {
    return `${SimulationApi.baseUrl}backend/api`;
  }

  // Only the simulation CRUD endpoints move to v2; loan, offer and schedule endpoints stay on the unversioned path.
  private get versionedApiUrl(): string {
    return EnvironmentService.Environment.isBackendSimV2 ? `${this.apiUrl}/v2` : this.apiUrl;
  }

  public async createSimulation(request: MortgageCreateSimulationRequest): Promise<MortgageCreateSimulationResponse> {
    const response = await this.client.post<MortgageCreateSimulationResponse>(
      `${this.versionedApiUrl}/simulation`,
      SimulationApi.withoutEmptyHouseholds(request),
    );
    return response.data;
  }

  public async getSimulationById(simulationId: string): Promise<MortgageGetSimulationByIdResponse> {
    const response = await this.client.get<MortgageGetSimulationByIdResponse>(`${this.versionedApiUrl}/simulation/${simulationId}`);
    return response.data;
  }

  public async updateSimulationById(
    simulationId: string,
    request: MortgageUpdateSimulationByIdRequest,
  ): Promise<MortgageUpdateSimulationByIdResponse> {
    const response = await this.client.post<MortgageUpdateSimulationByIdResponse>(
      `${this.versionedApiUrl}/simulation/${simulationId}`,
      SimulationApi.withoutEmptyHouseholds(request),
    );
    return response.data;
  }

  // Every keystroke in the user-input panel fires this, so a newer call cancels the one still in flight.
  public async updatePersistentSimulationById(
    simulationId: string,
    request: MortgageUpdatePersistentSimulationByIdRequest,
  ): Promise<MortgageUpdatePersistentSimulationByIdResponse> {
    this.persistentSimulationRequest?.cancel('Superseded by a newer persistent simulation update.');
    const source = Axios.CancelToken.source();
    this.persistentSimulationRequest = source;
    try {
      const response = await this.client.post<MortgageUpdatePersistentSimulationByIdResponse>(
        `${this.versionedApiUrl}/persistent/simulation/${simulationId}`,
        SimulationApi.withoutEmptyHouseholds(request),
        {cancelToken: source.token,},
      );
      return response.data;
    } finally {
      if (this.persistentSimulationRequest === source) {
        this.persistentSimulationRequest = null;
      }
    }
  }

  public async isLoanAvailable(loanId: string, request: MortgageIsLoanAvailableRequest): Promise<MortgageIsLoanAvailableResponse> {
    const response = await this.client.post<MortgageIsLoanAvailableResponse>(
      `${this.apiUrl}/simulation/loan/${loanId}/isAvailable`,
      SimulationApi.withoutEmptyHouseholds(request),
    );
    return response.data;
  }

  public async duplicateLoan(loanId: string, request: MortgageDuplicateLoanRequest): Promise<MortgageDuplicateLoanResponse> {
    const response = await this.client.post<MortgageDuplicateLoanResponse>(`${this.apiUrl}/simulation/loan/${loanId}/duplicate`, request);
    return response.data;
  }

  public async removeDuplicate(loanId: string, request: MortgageRemoveDuplicateRequest): Promise<MortgageRemoveDuplicateResponse> {
    const response = await this.client.post<MortgageRemoveDuplicateResponse>(`${this.apiUrl}/simulation/loan/${loanId}/removeDuplicate`, request);
    return response.data;
  }

  public async getOffersByIds(simulationId: string, request: MortgageGetOffersByIdsRequest): Promise<MortgageGetOffersByIdsResponse> {
    const response = await this.client.post<MortgageGetOffersByIdsResponse>(`${this.apiUrl}/simulation/${simulationId}/offers`, request);
    return response.data;
  }

  public async pinOffer(simulationId: string, offerId: string): Promise<MortgagePinOfferResponse> {
    const response = await this.client.put<MortgagePinOfferResponse>(`${this.apiUrl}/simulation/${simulationId}/pinned-offers/${offerId}`);
    return response.data;
  }

  public async unpinOffer(simulationId: string, offerId: string): Promise<MortgageUnpinOfferResponse> {
    const response = await this.client.delete(`${this.apiUrl}/simulation/${simulationId}/pinned-offers/${offerId}`);
    return response.data;
  }

  public async selectOffer(simulationId: string, offerId: string): Promise<MortgageSelectOfferResponse> {
    const response = await this.client.put<MortgageSelectOfferResponse>(`${this.apiUrl}/simulation/${simulationId}/selected-offers/${offerId}`);
    return response.data;
  }

  public async unselectOffer(simulationId: string, offerId: string): Promise<MortgageUnselectOfferResponse> {
    const response = await this.client.delete(`${this.apiUrl}/simulation/${simulationId}/selected-offers/${offerId}`);
    return response.data;
  }

  public async getSchedule(request: MortgageGetScheduleRequest): Promise<MortgageGetScheduleResponse> {
    const response = await this.client.post<MortgageGetScheduleResponse>(`${this.apiUrl}/schedule`, request);
    return response.data;
  }

  public async getHomeStartAssumptions(request: MortgageGetHomeStartAssumptionsRequest): Promise<MortgageGetHomeStartAssumptionsResponse> {
    const response = await this.client.post<MortgageGetHomeStartAssumptionsResponse>(`${this.apiUrl}/home-start-assumptions`, request);
    return response.data;
  }

  public addOffersToDeal(dealId: number, request: MortgageAddOffersToDealRequest): Promise<void> {
    return SimulationApi.addOffersToDeal(dealId, request.simulationId, request.simulationInput, request.offers);
  }
}

export default new MortgageSimulationApi();
